// Mock data for Manjunath Gangadhar "ward" Portfolio - V4 Poetic Glass World

export const portfolioData = {
  identity: {
    name: "Manjunath Gangadhar",
    alias: "ward",
    tagline: "I build machines that think, ride roads that breathe, and tend worlds that grow.",
    email: "wardhds121@gmail.com",
    linkedin: "https://www.linkedin.com/in/manjunath-gangadhar-56b5a2192"
  },
  
  about: {
    heading: "About",
    subtitle: "a journal, not a résumé",
    poeticBio: `I am Manjunath Gangadhar — ward, among many who share my name but none who share my path.

By day I speak to data, coaxing patterns from silence. By night I tend small living worlds — moss and water, root and glass — and ride roads that end where maps grow uncertain.

I build applications that think. I write code that feels. I read novels where mystery is the only law.

This is not a portfolio. It is a journal left open on a windowsill, in the rain.`,
    ktmQuote: "I ride the edges where roads forget their names."
  },
  
  techStack: {
    heading: "Tech Stack",
    subtitle: "tools of the craft",
    items: [
      { name: "Power BI", category: "Analytics" },
      { name: "Azure", category: "Cloud" },
      { name: "Python", category: "Programming" },
      { name: "ETL", category: "Data Engineering" },
      { name: "Salesforce CPQ", category: "CRM" },
      { name: "Sales Cloud", category: "CRM" },
      { name: "SAP BO", category: "Business Intelligence" },
      { name: "AI/ML", category: "Intelligence" },
      { name: "App Development", category: "Development" },
      { name: "Vibe Coding", category: "Philosophy" }
    ]
  },
  
  hobbies: {
    terrarium: {
      heading: "Living Glass Worlds",
      subtitle: "the gardens I keep",
      terrariumTitle: "Terrarium",
      terrariumContent: `A terrarium is an act of god-like patience. You build a world no larger than a bowl — choose the soil layer by layer, place each stone as if carving geography, watch moss colonise glass over weeks. It teaches the only lesson worth knowing: everything grows at its own speed, or not at all.

I have kept terrariums since I began keeping stillness.`,
      aquariumTitle: "Aquarium",
      aquariumContent: `An aquarium is a meditation you can look at. Water moves the way thought moves — in currents you cannot see, only feel. Fish have no concept of the glass. They live in their entire world.

I maintain mine with the same care I give to code: small adjustments, patient observation, and the understanding that some things cannot be forced to grow.`
    },
    
    novels: {
      heading: "The Inner Forest",
      subtitle: "worlds lived in books",
      featuredNovel: {
        title: "Lord of the Mysteries",
        genre: "Cosmic Horror • Mystery • Taoist Philosophy",
        mood: `A story built like a labyrinth — every answer is a deeper question. Zhou Mingrui wakes in a strange world where gods are dead and mysteries are currency. It reads the way fog feels: heavy, beautiful, and full of things you cannot name.`,
        personalNote: `I read this the way some people pray — in the hours before sleep, when the world has gone quiet enough to hear it.`
      },
      verse: {
        title: "A verse by ward",
        content: `Rain against glass —
the mountain does not explain itself.
Nor does the coder, to the code.`,
        attribution: "— ward, 2025"
      },
      philosophy: `I believe that stories are the only true maps. Fiction, especially dark or mysterious fiction, teaches empathy for realities that don't exist — which is the only training ground for empathy for ones that do. Every novel I've finished has left something behind in the code I write afterward.`
    },
    
    ktm: {
      heading: "The Open Road",
      subtitle: "where maps run out",
      philosophy: `The KTM ADV 390 is not a machine. It is a philosophy with wheels.

I ride to find the roads that do not appear on any map — the ones that start as tracks and end as silence. Adventure riding is the closest thing I know to meditation at velocity: full presence, no thought, just the next curve and the one after it.

Every ride teaches something the office cannot. The road does not care about your deadlines. It only asks: are you here, right now?`,
      closingQuote: "I ride the edges where roads forget their names."
    },
    
    ai: {
      heading: "The Thinking Machine",
      subtitle: "building minds",
      content: `I came to AI from data. I stay because of wonder.

There is a moment in every model's training where it begins to surprise the person who built it. That moment — when the machine says something true that you didn't teach it — is the closest I've come to watching something think.

Vibe coding is not sloppiness. It is trust in intuition, in the AI collaborator, in the idea that sometimes the fastest path to truth is to stop planning and simply begin.

The most beautiful thing I ever built was a system that surprised me.`
    }
  },
  
  inspiredBy: {
    heading: "Inspired By",
    subtitle: "philosophies that shaped the work",
    quotes: [
      {
        text: "The world is more mysterious than it has any right to be.",
        source: "Lord of the Mysteries (Cuttlefish That Loves Diving)"
      },
      {
        text: "The forest never stops. Even in stillness, it breathes. Build things that breathe.",
        source: "ward, personal note"
      },
      {
        text: "Code is not logic. It is language. And like all language, it has a tone.",
        source: "on vibe coding"
      },
      {
        text: "The moss does not hurry. The moss does not rest.",
        source: "after Lao Tzu"
      },
      {
        text: "The machine that wonders is more interesting than the machine that answers.",
        source: "ward"
      }
    ]
  },
  
  contact: {
    heading: "Contact",
    subtitle: "Leave a word. The forest remembers.",
    buttonText: "Send into the forest",
    successMessage: {
      title: "Your words have been received, traveller.",
      subtitle: "The forest remembers every name."
    }
  },
  
  footer: {
    quote: "In the space between code and silence, something listens.",
    year: new Date().getFullYear()
  }
};

// Theme configurations based on time of day
export const themes = {
  morning: {
    name: "Morning",
    hours: { start: 5, end: 12 },
    rain: { count: 60, speed: 2.0, length: 11, opacity: 0.22 }
  },
  afternoon: {
    name: "Afternoon", 
    hours: { start: 12, end: 18 },
    rain: { count: 100, speed: 3.5, length: 17, opacity: 0.38 }
  },
  night: {
    name: "Night",
    hours: { start: 18, end: 5 },
    rain: { count: 160, speed: 6.0, length: 27, opacity: 0.52 },
    lightning: true
  }
};

export const getCurrentTheme = () => {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) return themes.morning;
  if (hour >= 12 && hour < 18) return themes.afternoon;
  return themes.night;
};
